import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { needsConsent, safeNextPath } from '../auth/postLoginRoute'
import { know } from '../api/know'
import { ApiError } from '../api/client'
import { CONSENT_ACCEPT_LABEL, ConsentNotice } from '../components/ConsentNotice'

/**
 * `/consent` — gate del orden CONSENTIMIENTO → CUESTIONARIO en la web (doc
 * 32 §4). Acá rebota `RequireConsent` (sobre `/data/:code`) y el `?next=` de
 * una invitación cuando el jugador todavía no firmó, con el destino colgado
 * en `?next=`: al aceptar seguimos viaje solos.
 *
 * NO está en el camino de juego: entrar al vr-app no pasa por acá (el
 * consentimiento se firma adentro del vr-app después de "Comenzar", decisión
 * de mg 2026-09-07). Vivía embebido en la vieja HomePage, que hoy es el
 * bridge al lobby (`pages/PlayBridge.tsx`).
 *
 * El texto es el mismo que firma el modo tablet (`ConsentNotice`).
 */
export function ConsentGate() {
  const { user, refresh } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const next = safeNextPath(new URLSearchParams(location.search).get('next'))

  // Ya firmado (o staff, eximido): no hay gate que mostrar.
  if (user && !needsConsent(user)) {
    return <Navigate to={next ?? '/'} replace />
  }

  async function accept() {
    setBusy(true)
    setError(null)
    try {
      await know.recordMyConsent()
      // Tras el POST refrescamos /auth/me para que `consent_ok` se actualice
      // a nivel global y el gate se cierre solo.
      await refresh()
      navigate(next ?? '/', { replace: true })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No se pudo registrar el consentimiento.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-lg px-lg py-2xl">
      <div className="rounded-md border border-primary/40 bg-surface-elevated p-lg shadow-[0_0_34px_rgba(226,90,27,0.10)]">
        <h1>Consentimiento</h1>
        <p className="m-0 text-sm leading-relaxed text-text-secondary">
          Antes de responder necesitamos tu consentimiento para participar.
        </p>
      </div>

      <div className="card flex flex-col gap-lg border-primary/30">
        <ConsentNotice />

        {error && <div className="banner-err">{error}</div>}

        <button
          type="button"
          className="btn-primary"
          disabled={busy}
          onClick={() => void accept()}
        >
          {busy ? 'Guardando…' : CONSENT_ACCEPT_LABEL}
        </button>
      </div>
    </div>
  )
}
